/**
 * Simple Mobile Refresh Button
 * Provides a clean, native-like refresh experience for mobile users
 */

import React, { useState } from 'react';
import { RefreshCw } from 'lucide-react';
import { cn } from '@/lib/utils';

interface MobileRefreshButtonProps {
  onRefresh?: () => Promise<void>;
  className?: string;
}

export const MobileRefreshButton: React.FC<MobileRefreshButtonProps> = ({
  onRefresh,
  className
}) => {
  const [isRefreshing, setIsRefreshing] = useState(false);

  const handleRefresh = async () => {
    if (isRefreshing) return;
    
    setIsRefreshing(true);
    
    try {
      if (onRefresh) {
        await onRefresh();
      } else {
        // Default behavior: simple page reload
        window.location.reload();
      }
    } catch (error) {
      // Fallback to page reload on error
      window.location.reload();
    } finally {
      setIsRefreshing(false);
    }
  };

  return (
    <button
      onClick={handleRefresh}
      disabled={isRefreshing}
      className={cn(
        'min-[938px]:hidden p-2 rounded-lg hover:bg-gray-100 transition-colors duration-200',
        'min-h-[44px] min-w-[44px] touch-manipulation active:opacity-70',
        'flex items-center justify-center',
        isRefreshing && 'opacity-50 cursor-not-allowed',
        className
      )}
      style={{ touchAction: 'manipulation' }}
      aria-label="Refresh page"
    >
      <RefreshCw 
        className={cn(
          'w-5 h-5 text-gray-600',
          isRefreshing && 'animate-spin'
        )} 
      />
    </button>
  );
};

export default MobileRefreshButton;
